import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getImageUri } from "../core/utils/imageLoad";
import { useCategories } from "../hooks/category"; // Ajusta la ruta a donde guardaste el hook
import type { AccordionservicesProps } from "../core/types/tsx/components/IconsCarouselCategory.types";
// Importamos el tipo Category original
import type { CategoryResponse } from "../core/types/category"; // Ajusta la ruta según tu estructura

import defaultIconUrl from "../icons/limpieza.svg";

const getVisibleCountForViewport = (
	viewportWidth: number,
	totalCategories: number,
	defaultVisibleCount: number,
) => {
	if (totalCategories <= 0) return 1;

	if (viewportWidth < 640) {
		const mobileTarget = 4;
		return Math.min(totalCategories, mobileTarget);
	}

	if (viewportWidth < 1024) {
		const tabletTarget = Math.max(4, Math.min(5, defaultVisibleCount));
		return Math.min(totalCategories, tabletTarget);
	}

	return Math.min(totalCategories, Math.max(1, defaultVisibleCount));
};

export function Accordionservices({ initialVisibleCount = 6 }: AccordionservicesProps) {
	// 1. Usamos el hook para obtener las categorías
	const { data: categoryData, loading } = useCategories();
	// 2. Procesamos la data para calcular la URL completa del icono.
	// Retornamos un array de objetos que extiende o mantiene la estructura necesaria.
	const categories = useMemo(() => {
		if (!categoryData || !Array.isArray(categoryData)) return [];

		return categoryData.map((cat: CategoryResponse) => {
			// Creamos una propiedad temporal para la URL completa
			const fullIconUrl = getImageUri(cat.icon_category) ?? "";

			return {
				...cat, // Mantenemos todas las propiedades de Category (id, name_category, etc.)
				fullIconUrl, // Agregamos la URL procesada
			};
		});
	}, [categoryData]);

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const isPausedRef = useRef(false);
	const autoScrollInterval = useRef<ReturnType<typeof setInterval> | null>(null);
	const [viewportWidth, setViewportWidth] = useState<number>(() => window.innerWidth);

	// --- LÓGICA DEL CARRUSEL (Scroll Infinito) ---

	const visibleCount = getVisibleCountForViewport(
		viewportWidth,
		categories.length,
		Math.max(1, initialVisibleCount),
	);
	const cardGapPx = viewportWidth < 640 ? 12 : 16;
	const shouldLoop = categories.length > visibleCount;
	// Duplicamos el array para el efecto visual de loop
	const loopedCategories = shouldLoop ? [...categories, ...categories] : categories;

	const getScrollMetrics = useCallback(() => {
		if (!scrollRef.current) return { unitStep: 0, pageStep: 0, halfWidth: 0 };

		const el = scrollRef.current;
		const firstCard = el.firstElementChild as HTMLElement | null;
		if (!firstCard) return { unitStep: 0, pageStep: 0, halfWidth: 0 };

		const secondCard = el.children[1] as HTMLElement | undefined;
		const unitStep = secondCard
			? secondCard.offsetLeft - firstCard.offsetLeft
			: firstCard.offsetWidth;

		const nextPageAnchor = el.children[visibleCount] as HTMLElement | undefined;
		const pageStep = nextPageAnchor
			? nextPageAnchor.offsetLeft - firstCard.offsetLeft
			: unitStep * visibleCount;

		const secondHalfAnchor = el.children[categories.length] as
			| HTMLElement
			| undefined;
		const halfWidth = secondHalfAnchor
			? secondHalfAnchor.offsetLeft - firstCard.offsetLeft
			: unitStep * categories.length;

		return { unitStep, pageStep, halfWidth };
	}, [categories.length, visibleCount]);

	useEffect(() => {
		const onResize = () => {
			setViewportWidth(window.innerWidth);
		};

		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	const scrollOne = useCallback(
		(direction: "left" | "right") => {
			if (!scrollRef.current) return;
			if (categories.length === 0) return;

			const { unitStep, pageStep, halfWidth } = getScrollMetrics();
			if (unitStep <= 0 || pageStep <= 0) return;

			const el = scrollRef.current;

			if (shouldLoop && direction === "left" && el.scrollLeft <= pageStep / 2) {
				el.scrollLeft += halfWidth;
			}

			const currentPage = Math.round(el.scrollLeft / pageStep);
			const nextPage = direction === "left" ? currentPage - 1 : currentPage + 1;
			const next = nextPage * pageStep;

			el.scrollTo({ left: Math.round(next), behavior: "smooth" });

			if (shouldLoop && direction === "right") {
				window.setTimeout(() => {
					const node = scrollRef.current;
					if (!node) return;
					// Ajuste silencioso para loop infinito
					if (node.scrollLeft >= halfWidth + pageStep / 2) {
						node.scrollLeft -= halfWidth;
					}
				}, 350);
			}
		},
		[categories.length, getScrollMetrics, shouldLoop],
	);

	// Auto-scroll
	useEffect(() => {
		if (categories.length <= visibleCount) {
			if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
			return;
		}

		autoScrollInterval.current = setInterval(() => {
			if (!isPausedRef.current) {
				scrollOne("right");
			}
		}, 3000);

		return () => {
			if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
		};
	}, [categories.length, visibleCount, scrollOne]);

	// Reset scroll cuando cambia la data
	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollLeft = 0;
		}
	}, [categories.length]);

	// --- RENDERIZADO ---

	if (loading && categories.length === 0) {
		return (
			<div className='flex w-full items-center justify-center p-10 text-neutral-400 text-sm'>
				<span>Cargando servicios...</span>
			</div>
		);
	}

	return (
		<div className='w-full max-w-full overflow-hidden'>
			<div
				className='relative overflow-hidden'
				onMouseEnter={() => {
					isPausedRef.current = true;
				}}
				onMouseLeave={() => {
					isPausedRef.current = false;
				}}
				onTouchStart={() => {
					isPausedRef.current = true;
				}}
				onTouchEnd={() => {
					isPausedRef.current = false;
				}}
			>
				<button
					type='button'
					aria-label='Anterior'
					onClick={() => scrollOne("left")}
					className='absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-md ring-1 ring-black/5 transition hover:bg-white'
				>
					<ChevronLeft className='h-6 w-6 text-neutral-700 cursor-pointer' />
				</button>

				<div
					ref={scrollRef}
					className='carousel-scroll-container grid w-full max-w-full grid-flow-col gap-3 overflow-x-auto scroll-smooth overscroll-x-contain snap-x snap-mandatory px-3 pb-2 sm:gap-4 sm:px-10'
					style={{
						gridAutoColumns: `calc((100% - ${(visibleCount - 1) * cardGapPx}px) / ${visibleCount})`,
					}}
				>
					{loopedCategories.map((cat, index) => (
						<button
							// Usamos el ID original + index para las keys únicas en el loop
							key={`${cat.id || "cat"}-loop-${index}`}
							type='button'
							aria-label={cat.name_category}
							className='group inline-flex min-w-0 shrink-0 snap-start flex-col items-center justify-start gap-2 rounded-2xl transition-opacity duration-200'
						>
							<div className='flex h-12 w-12 items-center justify-center rounded-full bg-neutral-50 text-neutral-500 transition-colors group-hover:bg-primary/10 group-hover:text-primary sm:h-16 sm:w-16'>
								<img
									src={cat.fullIconUrl || defaultIconUrl}
									alt={cat.name_category}
									aria-hidden='true'
									draggable={false}
									className='h-7 w-7 object-contain sm:h-10 sm:w-10'
									onError={(e) => {
										e.currentTarget.src = defaultIconUrl;
									}}
								/>
							</div>
							<div className='flex flex-col items-center justify-start self-stretch'>
								<div className='line-clamp-2 self-stretch text-center text-xs font-normal leading-3.5 text-neutral-400 transition-colors group-hover:text-primary sm:text-sm sm:leading-4'>
									{cat.name_category}
								</div>
							</div>
						</button>
					))}

					{!loading && categories.length === 0 && (
						<div className='flex w-full items-center justify-center p-4 text-neutral-400 text-sm'>
							<span>No hay servicios disponibles.</span>
						</div>
					)}
				</div>

				<button
					type='button'
					aria-label='Siguiente'
					onClick={() => scrollOne("right")}
					className='absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-md ring-1 ring-black/5 transition hover:bg-white'
				>
					<ChevronRight className='h-6 w-6 text-neutral-700 cursor-pointer' />
				</button>
			</div>
		</div>
	);
}
